// Collects behavior-preserving helpers extracted from TiptapDocumentSB.
import {
  MAX_DESCRIBE_WORDS,
  MAX_EXPAND_WORDS,
  MAX_QUICK_EDIT_HIGHLIGHTED_WORDS,
  MAX_REWRITE_WORDS,
  MAX_VISUALIZE_WORDS,
  MIN_DESCRIBE_WORDS,
  MIN_EXPAND_WORDS,
  MIN_QUICK_EDIT_WORDS,
  MIN_REWRITE_WORDS,
  MIN_VISUALIZE_WORDS,
  normalizeParagraphSpacing,
} from "../../../../src/shared/constants";
import { clamp } from "lodash";

export const EXPIRED_FEATURE_ACCESS_MESSAGE = "Your free trial or membership has expired or is overdue. Subscribe to access AI features.";

type CountWordsFn = (text: string) => number;

type TextWindow = {
  consideration: string;
  windowStart: number;
};

type EditorComputedStyles = {
  fontFamily: string;
  fontSize: string;
  textIndent: string;
  lineHeight: string;
  paragraphSpacing: string;
  useTextIndent: boolean;
  dataTheme: string;
};

type EditorComputedStyleInput = {
  editorOptions: {
    fontSize: number;
    lineHeight?: number;
    textIndent: boolean;
    paragraphSpacing?: number;
  };
  fontFamily: string;
  theme: string;
};

type HighlightMenuState = {
  rewriteSelection?: boolean;
  describeSelection?: boolean;
  commandMenuSelection?: boolean;
  expandSelection?: boolean;
  relatedClusters?: unknown;
};

type HighlightMenuParams = {
  subscription: { hasAccessToFeatures: boolean };
  state: HighlightMenuState;
  selectionBounds: unknown;
  selectionRange?: { length?: number } | null;
  hasEditorContainer: unknown;
  tiptapViewHasFocus?: boolean;
  isPointerDragging?: boolean;
};

type RectLike = {
  top?: number;
  bottom?: number;
};

type StoryBibleActiveParams = {
  activeElement: Element | null;
  editorEndRect?: RectLike | null;
  centerColumnRect?: RectLike | null;
};

type ScrollContainerLike = {
  scrollHeight?: number;
  clientHeight?: number;
  scrollTop?: number;
  getBoundingClientRect: () => { bottom: number };
};

type ProgrammaticScrollParams = {
  storyBibleActive: boolean;
  editorSectionElement?: ScrollContainerLike | null;
  viewportHeight?: number;
};

type CardPageCursor = {
  id: string;
};

type CardQueryMap = Record<string, boolean>;

type FirestoreCardDoc = {
  id: string;
  data: () => unknown;
};

type FirestoreCardSnapshot = {
  forEach: (callback: (doc: FirestoreCardDoc) => void) => void;
};

type CardModelConstructor<TCardModel = unknown> = new (id: string, data: unknown, hidden: boolean) => TCardModel;

type RightBarCard<TCardModel = unknown> = {
  model: TCardModel;
  doneStreaming: boolean;
  hidden: boolean;
  snapshot: FirestoreCardDoc;
  onRedo: null;
  loading: boolean;
  ready: boolean;
};

type FindAndReplaceState = {
  findAndReplaceOpen: boolean;
};

type ApplyFindAndReplaceParams = {
  findAndReplaceOpen: boolean;
  setState: (state: FindAndReplaceState) => void;
  setForceRenderSearchables: (forceRenderSearchables: boolean) => void;
};

type EmptyDocumentLinkingMenuParams = {
  textLength: number;
  existingCardsLoaded: boolean;
  newCardsLength: number;
  existingCardsLength: number;
  isCreatingNewBlankDocument: boolean;
  proseGeneratorActive: boolean;
};

type FeatureActionBlockReasonParams = {
  // Only a server-confirmed offer for this tool can waive the credit requirement.
  creditWaived?: boolean;
  subscription?: { hasAccessToFeatures: boolean } | null;
  loadingDocument: boolean;
  loadingMessage: string;
};

type Column = {
  current: number;
  min?: number;
  openMin: number;
  collapsedMin: number;
  collapsed: boolean;
};

type CenterColumn = {
  current: number;
  min: number;
};

type ColumnWidths = {
  left: Column;
  center: CenterColumn;
  right: Column;
};

type ColumnResizeParams = {
  columnWidths: ColumnWidths;
  viewportWidth: number;
};

type CenterColumnResizeState = {
  centerCurrent: number;
  hasResizedCenter: boolean;
};

type ColumnResizeState = {
  leftCurrent: number;
  centerCurrent: number;
  rightCurrent: number;
  rightCollapsed: boolean;
  hasResizedColumns: boolean;
};

type WindowResizeState =
  | {
      shouldCollapsePanels: false;
      centerCurrent: number;
    }
  | {
      shouldCollapsePanels: true;
      leftCollapsed: boolean;
      leftSaved: number;
      leftCurrent: number;
      rightCollapsed: boolean;
      rightSaved: number;
      rightCurrent: number;
      hasResizedColumns: boolean;
      centerCurrent: number;
    };

type SelectionRange = {
  length: number;
  text: string;
};

type TiptapSelectionEditor = {
  isDestroyed?: boolean;
  view?: { state?: unknown } | null;
};

type HighlightedWordCountState = {
  highlightedWordCount: number | null;
};

type SelectionActionState = {
  rewriteSelection: boolean;
  expandSelection: boolean;
  visualizeSelection: boolean;
  describeSelection: boolean;
  commandMenuSelection: boolean;
  shouldFetchRelatedWords: boolean;
  shouldSendTextSelectedSignal: boolean;
};

type CollapsedSelectionState<TRange> = {
  expandSelection: false;
  describeSelection: false;
  rewriteSelection: false;
  visualizeSelection: false;
  commandMenuSelection: false;
  relatedClusters: null;
  selectionRange: TRange;
};

type ClearedSelectionState = {
  selectionRange: null;
  expandSelection: false;
  describeSelection: false;
  rewriteSelection: false;
  visualizeSelection: false;
  commandMenuSelection: false;
  relatedClusters: null;
  selection: null;
  highlightedWordCount: null;
};

type TextChangeResetState = {
  expandSelection: false;
  describeSelection: false;
  visualizeSelection: false;
  rewriteSelection: false;
  commandMenuSelection: false;
  relatedClusters: null;
  commandModeState?: "off";
};

type TextChangeProjectUiState = {
  hideLinkingMenu: boolean;
  showNewDocumentCTAs: boolean;
};

/**
 * Builds the token-sized text window immediately before a document index.
 *
 * @param {string} text Full document text.
 * @param {number} index Document index where the backward window should end.
 * @param {number} targetTokens Approximate token budget for the window.
 * @returns {{ consideration: string, windowStart: number }} Selected text and its document-relative start index.
 */
export function getBackwardTokenWindow(text: string, index: number, targetTokens: number): TextWindow {
  let currPosition = text.length - 1;
  let windowStart = 0;
  const windowEnd = index - 1;
  let abort = false;
  let wordCount = 0;
  let prevChar: string | null = null;
  const tokensToWords = 1.4;

  for (currPosition; !abort; currPosition--) {
    const char = text[currPosition];

    if (char === "\n" || (char === " " && prevChar !== " ")) {
      wordCount++;
    }

    if (currPosition <= 0) {
      windowStart = 0;
      break;
    }

    if (wordCount * tokensToWords >= targetTokens) {
      windowStart = currPosition;
      abort = true;
    }

    prevChar = char;
  }

  return {
    consideration: text.substr(windowStart, windowEnd - windowStart + 1),
    windowStart,
  };
}

/**
 * Builds the approximate token-sized expansion window from the end of text.
 *
 * Preserves the legacy `substr(start, end)` length behavior used by the original component.
 * Changing this would alter Expand prompt context relative to the class implementation.
 *
 * @param {string} text Text to trim to the expansion context window.
 * @param {number} targetTokens Approximate token budget for the window.
 * @returns {string} Expansion context text.
 */
export function getExpandTokenWindow(text: string, targetTokens: number): string {
  let currPosition = text.length - 1;
  let windowStart = 0;
  const windowEnd = text.length - 1;
  let abort = false;
  let wordCount = 0;
  let prevChar: string | null = null;
  const tokensToWords = 1.4;

  for (currPosition; !abort; currPosition--) {
    const char = text[currPosition];

    if (char === "\n" || (char === " " && prevChar !== " ")) {
      wordCount++;
    }

    if (currPosition <= 0) {
      windowStart = 0;
      break;
    }

    if (wordCount * tokensToWords >= targetTokens) {
      windowStart = currPosition;
      abort = true;
    }

    prevChar = char;
  }

  return text.substr(windowStart, windowEnd);
}

/**
 * Builds a word-count-limited text window before a document index.
 *
 * @param {string} text Full document text.
 * @param {number} index Document index where the backward window should end.
 * @param {number} targetWords Maximum approximate number of words to include.
 * @returns {string} Backward context text.
 */
export function getBackwardWordWindow(text: string, index: number, targetWords: number): string {
  let currPosition = text.length - 1;
  let windowStart = 0;
  const windowEnd = index - 1;
  let abort = false;
  let wordCount = 0;
  let prevChar: string | null = null;

  for (currPosition; !abort; currPosition--) {
    const char = text[currPosition];

    if (char === "\n" || (char === " " && prevChar !== " ")) {
      wordCount++;
    }

    if (currPosition <= 0) {
      windowStart = 0;
      break;
    }

    if (wordCount >= targetWords) {
      windowStart = currPosition;
      abort = true;
    }

    prevChar = char;
  }

  return text.substr(windowStart, windowEnd - windowStart + 1);
}

/**
 * Builds a word-count-limited text window from the start of text.
 *
 * @param {string} text Text after the current cursor/selection.
 * @param {number} targetWords Maximum approximate number of words to include.
 * @returns {string} Forward context text.
 */
export function getForwardWordWindow(text: string, targetWords: number): string {
  let currPosition = 0;
  const windowStart = 0;
  let windowEnd = 0;
  let abort = false;
  let wordCount = 0;
  let prevChar: string | null = null;

  for (currPosition; !abort; currPosition++) {
    const char = text[currPosition];

    if (char === "\n" || (char === " " && prevChar !== " ")) {
      wordCount++;
    }

    if (currPosition >= text.length - 1) {
      windowEnd = text.length - 1;
      break;
    }

    if (wordCount >= targetWords) {
      windowEnd = currPosition;
      abort = true;
    }

    prevChar = char;
  }

  return text.substr(windowStart, windowEnd - windowStart + 1);
}

/**
 * Builds the describe-context text around a cursor by combining previous text and the next line.
 *
 * @param {string} text Text before the cursor.
 * @param {string} afterText Text after the cursor.
 * @param {number} index Cursor index in the original document text.
 * @returns {string} Context passed to describe actions.
 */
export function getDescriptionConsiderationFromText(text: string, afterText: string, index: number): string {
  let currPosition = text.length - 1;
  let windowStart = 0;
  const windowEnd = index - 1;
  let afterWindowEnd = 0;
  let abort = false;
  let wordCount = 0;
  let prevChar: string | null = null;

  for (currPosition; !abort; currPosition--) {
    const char = text[currPosition];

    if (char === " " && prevChar !== " ") {
      wordCount++;
    }

    if (wordCount > 200) {
      windowStart = currPosition + 1;
      break;
    }

    if (currPosition <= 0) {
      windowStart = 0;
      break;
    }
    prevChar = char;
  }

  currPosition = 0;
  for (currPosition; !abort; currPosition++) {
    const char = afterText[currPosition];

    if (char === "\n") {
      afterWindowEnd = currPosition - 1;
      break;
    }

    if (char === " " && prevChar !== " ") {
      wordCount++;
    }

    if (currPosition === afterText.length - 1 || currPosition > 1000) {
      afterWindowEnd = currPosition;
      break;
    }
    prevChar = char;
  }

  return text.substr(windowStart, windowEnd - windowStart + 1) + afterText.substr(0, afterWindowEnd + 1);
}

/**
 * Maps editor line-height options to the legacy CSS line-height multipliers.
 *
 * @param {number | undefined} lineHeightOption Stored editor line-height option.
 * @returns {number | undefined} CSS line-height multiplier.
 */
export function getEditorLineHeight(lineHeightOption?: number): number | undefined {
  switch (lineHeightOption || 1.0) {
    case 1.0:
      return 1.3;
    case 1.5:
      return 1.7;
    case 2.0:
      return 2.2;
  }
}

/**
 * Maps the text-indent editor option to the CSS text-indent value.
 *
 * @param {boolean} textIndent Whether paragraph indentation is enabled.
 * @returns {string} CSS text-indent value.
 */
export function getEditorTextIndent(textIndent: boolean): string {
  return textIndent ? "1.5em" : "0";
}

/**
 * Maps the paragraph-spacing editor option (1x / 1.5x / 2x relative to
 * line-height) to the EXTRA CSS margin-bottom in px.
 *
 * Adjacent paragraphs already sit about one line-height apart with no margin
 * (same as consecutive lines). So 1x → 0 extra space, 1.5x → 0.5×line-height
 * extra, 2x → 1×line-height extra. Px rather than em because the value is
 * consumed inside heading margins too, where em would resolve against the
 * heading's larger font-size and inflate the gap.
 *
 * @param {number | undefined} paragraphSpacing Stored option (1 / 1.5 / 2).
 * @param {number | undefined} fontSize Body font size in px (editorOptions.fontSize).
 * @param {number | undefined} lineHeightOption Stored line-height option (1 / 1.5 / 2).
 * @returns {string} CSS margin-bottom value.
 */
export function getEditorParagraphSpacing(
  paragraphSpacing?: number,
  fontSize?: number,
  lineHeightOption?: number
): string {
  // Always a px-typed length ("0px", never bare "0"): the value is used
  // inside calc()/max() where a unitless zero invalidates the expression.
  const multiplier = normalizeParagraphSpacing(
    typeof paragraphSpacing === "number" && isFinite(paragraphSpacing) ? paragraphSpacing : undefined
  );
  const extraLineHeights = Math.max(0, multiplier - 1);
  if (!extraLineHeights) return "0px";
  const lineHeightEm = getEditorLineHeight(lineHeightOption) || 1.3;
  const bodyPx = typeof fontSize === "number" && isFinite(fontSize) && fontSize > 0 ? fontSize : 20;
  return `${Math.round(extraLineHeights * lineHeightEm * bodyPx * 100) / 100}px`;
}

/**
 * Derives the editor style object shared by the rendered editor and iframe styles.
 *
 * @param {{ editorOptions: { fontSize: number, lineHeight?: number, textIndent: boolean }, fontFamily: string, theme: string }} params Style inputs.
 * @returns {{ fontFamily: string, fontSize: string, textIndent: string, lineHeight: string, useTextIndent: boolean, dataTheme: string }} Computed editor styles.
 */
export function getEditorComputedStyles({ editorOptions, fontFamily, theme }: EditorComputedStyleInput): EditorComputedStyles {
  const lineHeight = getEditorLineHeight(editorOptions.lineHeight);

  return {
    fontFamily,
    fontSize: `${editorOptions.fontSize}px`,
    textIndent: getEditorTextIndent(editorOptions.textIndent),
    lineHeight: `${lineHeight}em`,
    paragraphSpacing: getEditorParagraphSpacing(
      editorOptions.paragraphSpacing,
      editorOptions.fontSize,
      editorOptions.lineHeight
    ),
    useTextIndent: editorOptions.textIndent,
    dataTheme: theme,
  };
}

/**
 * Applies computed editor styles to the editor DOM element.
 *
 * Mutates the provided element's inline styles, the live ProseMirror root
 * styles, and the `indented` class.
 *
 * @param {HTMLElement | null | undefined} editorElement Editor DOM element.
 * @param {{ fontFamily: string, fontSize: string, lineHeight: string, useTextIndent: boolean }} editorComputedStyles Computed style values.
 */
export function applyEditorComputedStyles(
  editorElement: HTMLElement | null | undefined,
  editorComputedStyles: Pick<EditorComputedStyles, "fontFamily" | "fontSize" | "lineHeight" | "paragraphSpacing" | "useTextIndent">
): void {
  if (!editorElement) {
    return;
  }

  const styledElements = [editorElement, editorElement.querySelector<HTMLElement>(".ProseMirror")].filter(Boolean);

  styledElements.forEach((element) => {
    element.style.fontFamily = editorComputedStyles.fontFamily;
    element.style.fontSize = editorComputedStyles.fontSize;
    element.style.lineHeight = editorComputedStyles.lineHeight;
  });

  // Paragraph spacing renders via var(--sw-paragraph-spacing) in
  // editorContentStyles.js; only the property on #editor needs updating.
  // Px-typed even at zero — the heading rule divides it inside calc().
  editorElement.style.setProperty("--sw-paragraph-spacing", editorComputedStyles.paragraphSpacing || "0px");

  if (editorComputedStyles.useTextIndent) {
    editorElement.classList.add("indented");
  } else {
    editorElement.classList.remove("indented");
  }
}

/**
 * Determines whether the floating highlight menu should be visible for the current selection.
 *
 * @param {object} params Selection and editor state.
 * @returns {boolean} Whether the highlight menu should render.
 */
export function shouldShowHighlightMenu({
  subscription,
  state,
  selectionBounds,
  selectionRange,
  hasEditorContainer,
  tiptapViewHasFocus,
  isPointerDragging,
}: HighlightMenuParams): boolean | null | undefined {
  return (
    ((subscription.hasAccessToFeatures &&
      (state.rewriteSelection || //
        state.describeSelection || //
        state.commandMenuSelection || //
        state.expandSelection)) || //
      state.relatedClusters) &&
    selectionBounds &&
    (selectionRange?.length ?? 0) > 0 &&
    hasEditorContainer &&
    tiptapViewHasFocus &&
    !isPointerDragging
  ) as boolean | null | undefined;
}

/**
 * Checks whether the editor end marker has scrolled above the center-column viewport.
 *
 * @param {{ bottom: number } | null | undefined} editorEndRect Editor end marker bounds.
 * @param {{ top: number } | null | undefined} centerColumnRect Center column bounds.
 * @returns {boolean} Whether the editor end marker is above the visible column.
 */
export function isEditorEndAboveViewport(editorEndRect?: RectLike | null, centerColumnRect?: RectLike | null): boolean {
  if (!editorEndRect || !centerColumnRect) {
    return false;
  }

  return Number(editorEndRect.bottom) < Number(centerColumnRect.top);
}

/**
 * Determines whether user focus/scroll position should be treated as Story Bible interaction.
 *
 * @param {{ activeElement: Element, editorEndRect: object, centerColumnRect: object }} params Active element and viewport bounds.
 * @returns {boolean} Whether Story Bible is currently considered active.
 */
export function getIsUserInStoryBible({ activeElement, editorEndRect, centerColumnRect }: StoryBibleActiveParams): boolean {
  const storyBibleContainer = activeElement?.closest(".story-bible-component-container");
  const isElementInStoryBible = storyBibleContainer !== null && storyBibleContainer !== undefined;

  return isElementInStoryBible || isEditorEndAboveViewport(editorEndRect, centerColumnRect);
}

/**
 * Determines whether a programmatic scroll reached the editor/Story Bible boundary.
 *
 * @param {{ storyBibleActive: boolean, editorSectionElement: Element, viewportHeight?: number }} params Scroll context.
 * @returns {boolean} Whether the scroll position is near the expected bottom boundary.
 */
export function isProgrammaticScrollAtBottom({ storyBibleActive, editorSectionElement, viewportHeight }: ProgrammaticScrollParams): boolean {
  if (storyBibleActive) {
    const viewportHeightValue = Number(viewportHeight);
    const thresholdTop = viewportHeightValue - 75;
    const thresholdBottom = viewportHeightValue + 150;
    const editorBottom = editorSectionElement?.getBoundingClientRect().bottom;

    return thresholdTop <= Number(editorBottom) && thresholdBottom >= Number(editorBottom);
  }

  const verticalMax = Number(editorSectionElement?.scrollHeight) - Number(editorSectionElement?.clientHeight);

  return Number(editorSectionElement?.scrollTop) >= verticalMax - 130;
}

/**
 * Checks whether an async document-switch callback still belongs to the current switch.
 *
 * @param {number} currentVersion Latest document switch version.
 * @param {number} capturedVersion Version captured when async work started.
 * @returns {boolean} Whether the captured work is still current.
 */
export function isCurrentDocumentSwitchVersion(currentVersion: number, capturedVersion: number): boolean {
  return currentVersion === capturedVersion;
}

/**
 * Determines whether a card page request has already been made for the same pagination cursor.
 *
 * @param {{ id: string } | null | undefined} startAfter Pagination cursor document.
 * @param {Record<string, boolean>} cardQueries Map of requested cursor IDs.
 * @returns {boolean} Whether the request should be skipped.
 */
export function shouldSkipCardPageRequest(startAfter: CardPageCursor | null | undefined, cardQueries: CardQueryMap): boolean {
  return Boolean(startAfter && cardQueries[startAfter.id]);
}

/**
 * Marks a card pagination cursor as requested.
 *
 * Mutates `cardQueries` in place to preserve the original request-tracking behavior.
 *
 * @param {{ id: string } | null | undefined} startAfter Pagination cursor document.
 * @param {Record<string, boolean>} cardQueries Map of requested cursor IDs.
 */
export function markCardPageRequested(startAfter: CardPageCursor | null | undefined, cardQueries: CardQueryMap): void {
  if (startAfter) {
    cardQueries[startAfter.id] = true;
  }
}

/**
 * Converts a Firestore card snapshot document into the right-bar card wrapper shape.
 *
 * @param {{ id: string, data: Function }} doc Firestore snapshot document.
 * @param {Function} CardModel Card model constructor.
 * @returns {object} Right-bar card wrapper.
 */
export function createRightBarCardFromSnapshot<TCardModel>(
  doc: FirestoreCardDoc,
  CardModel: CardModelConstructor<TCardModel>
): RightBarCard<TCardModel> {
  const cardModel = new CardModel(doc.id, doc.data(), true);

  return {
    model: cardModel,
    doneStreaming: true,
    hidden: true,
    snapshot: doc,
    onRedo: null,
    loading: false,
    ready: true,
  };
}

/**
 * Converts a Firestore query snapshot into right-bar card wrapper objects.
 *
 * @param {{ forEach: Function }} snapshot Firestore query snapshot.
 * @param {Function} CardModel Card model constructor.
 * @returns {object[]} Right-bar card wrappers.
 */
export function createRightBarCardsFromSnapshot<TCardModel>(
  snapshot: FirestoreCardSnapshot,
  CardModel: CardModelConstructor<TCardModel>
): Array<RightBarCard<TCardModel>> {
  const cards: Array<RightBarCard<TCardModel>> = [];

  snapshot.forEach((doc) => {
    cards.push(createRightBarCardFromSnapshot(doc, CardModel));
  });

  return cards;
}

/**
 * Builds state for appending loaded existing cards and marking the page load complete.
 *
 * @param {object[]} existingCards Already-rendered existing cards.
 * @param {object[]} loadedCards Newly loaded cards.
 * @returns {{ existingCards: object[], existingCardsLoaded: boolean }} Partial state update.
 */
export function getLoadedExistingCardsState<TCard>(existingCards: TCard[], loadedCards: TCard[]): { existingCards: TCard[]; existingCardsLoaded: boolean } {
  return {
    existingCards: [...existingCards, ...loadedCards],
    existingCardsLoaded: true,
  };
}

/**
 * Builds state for prepending a newly generated card and selecting the new-card tab.
 *
 * @param {object[]} newCards Existing generated cards.
 * @param {object} card Card to prepend.
 * @returns {{ newCards: object[], rightTab: number }} Partial state update.
 */
export function getPrependedNewCardState<TCard>(newCards: TCard[], card: TCard): { newCards: TCard[]; rightTab: number } {
  return {
    newCards: [card, ...newCards],
    rightTab: 0,
  };
}

/**
 * Builds the state update for find/replace visibility.
 *
 * @param {boolean} findAndReplaceOpen Whether find/replace should be open.
 * @returns {{ findAndReplaceOpen: boolean }} Partial state update.
 */
export function getFindAndReplaceOpenState(findAndReplaceOpen: boolean): FindAndReplaceState {
  return { findAndReplaceOpen };
}

/**
 * Applies find/replace visibility and mirrors it into Story Bible searchable rendering.
 *
 * Calls both state/update callbacks to preserve the component's coupled UI behavior.
 *
 * @param {{ findAndReplaceOpen: boolean, setState: Function, setForceRenderSearchables: Function }} params Update callbacks and desired visibility.
 */
export function applyFindAndReplaceOpenState({ findAndReplaceOpen, setState, setForceRenderSearchables }: ApplyFindAndReplaceParams): void {
  setState(getFindAndReplaceOpenState(findAndReplaceOpen));
  setForceRenderSearchables(findAndReplaceOpen);
}

/**
 * Determines whether an empty synced document should show the linking menu.
 *
 * @param {object} params Synced editor/card state.
 * @returns {boolean} Whether to show the linking menu.
 */
export function shouldShowLinkingMenuOnEditorSynced({
  textLength,
  existingCardsLoaded,
  newCardsLength,
  existingCardsLength,
  isCreatingNewBlankDocument,
  proseGeneratorActive,
}: EmptyDocumentLinkingMenuParams): boolean {
  return (
    textLength === 0 &&
    existingCardsLoaded &&
    newCardsLength === 0 &&
    existingCardsLength === 0 &&
    !isCreatingNewBlankDocument &&
    !proseGeneratorActive
  );
}

/**
 * Returns the user-facing block reason for AI feature actions, if one applies.
 *
 * @param {{ subscription?: { hasAccessToFeatures: boolean }, creditWaived?: boolean, loadingDocument: boolean, loadingMessage: string }} params Subscription and loading state.
 * @returns {string | null} Block reason, or null when the action can continue.
 */
export function getFeatureActionBlockReason({ subscription, creditWaived = false, loadingDocument, loadingMessage }: FeatureActionBlockReasonParams): string | null {
  if (subscription && !subscription.hasAccessToFeatures && !creditWaived) {
    return EXPIRED_FEATURE_ACCESS_MESSAGE;
  }

  if (loadingDocument) {
    return loadingMessage;
  }

  return null;
}

/**
 * Computes the center-column width when only the editor column is resized.
 *
 * @param {{ columnWidths: object, viewportWidth: number }} params Current column widths and viewport width.
 * @returns {{ centerCurrent: number, hasResizedCenter: boolean }} Partial layout state.
 */
export function getCenterColumnResizeState({ columnWidths, viewportWidth }: ColumnResizeParams): CenterColumnResizeState {
  return {
    centerCurrent: clamp(viewportWidth - columnWidths.left.current - columnWidths.right.current, columnWidths.center.min, Infinity),
    hasResizedCenter: true,
  };
}

/**
 * Computes responsive left/center/right column widths for a viewport resize.
 *
 * Preserves the legacy right-panel collapse behavior when the center column can no longer fit.
 *
 * @param {{ columnWidths: object, viewportWidth: number }} params Current column widths and viewport width.
 * @returns {{ leftCurrent: number, centerCurrent: number, rightCurrent: number, rightCollapsed: boolean, hasResizedColumns: boolean }} Partial layout state.
 */
export function getColumnResizeState({ columnWidths, viewportWidth }: ColumnResizeParams): ColumnResizeState {
  const leftMin = columnWidths.left.collapsed ? columnWidths.left.collapsedMin : columnWidths.left.openMin;
  const centerMin = columnWidths.center.min;
  const rightMin = columnWidths.right.collapsed ? columnWidths.right.collapsedMin : columnWidths.right.openMin;
  const rightCollapsedMin = columnWidths.right.collapsedMin;
  let currentLeft = columnWidths.left.current;
  let currentCenter = columnWidths.center.current;
  let currentRight = columnWidths.right.current;
  let rightCollapsed = columnWidths.right.collapsed;
  const columnsTotal = currentLeft + currentCenter + currentRight;
  const diff = viewportWidth - columnsTotal;

  if (currentLeft > leftMin && currentRight > rightMin) {
    currentLeft += diff / 2;
    currentRight += diff / 2;
  } else if (currentLeft <= leftMin && currentRight > rightMin) {
    currentRight += diff;
  } else if (currentRight <= rightMin && currentLeft > leftMin) {
    currentLeft += diff;
  } else {
    currentCenter += diff;
    if (currentCenter <= centerMin) {
      currentRight = rightCollapsedMin;
      rightCollapsed = true;
      currentCenter = viewportWidth - currentLeft - currentRight;
    }
  }

  const total = currentLeft + currentCenter + currentRight;
  if (total !== viewportWidth) {
    currentCenter += viewportWidth - total;
  }

  return {
    leftCurrent: clamp(currentLeft, leftMin, viewportWidth - centerMin - rightMin),
    centerCurrent: clamp(currentCenter, centerMin, viewportWidth - leftMin - rightMin),
    rightCurrent: clamp(currentRight, rightCollapsed ? rightCollapsedMin : rightMin, viewportWidth - leftMin - centerMin),
    rightCollapsed,
    hasResizedColumns: true,
  };
}

/**
 * Computes layout state for full window resizes, including side-panel collapse when needed.
 *
 * @param {{ columnWidths: object, viewportWidth: number }} params Current column widths and viewport width.
 * @returns {object} Partial layout state for the project store.
 */
export function getWindowResizeState({ columnWidths, viewportWidth }: ColumnResizeParams): WindowResizeState {
  const centerCurrent = viewportWidth - columnWidths.left.current - columnWidths.right.current;

  if (centerCurrent >= columnWidths.center.min) {
    return {
      shouldCollapsePanels: false,
      centerCurrent,
    };
  }

  const leftCurrent = columnWidths.left.collapsedMin;
  const rightCurrent = columnWidths.right.collapsedMin;

  return {
    shouldCollapsePanels: true,
    leftCollapsed: true,
    leftSaved: columnWidths.left.current,
    leftCurrent,
    rightCollapsed: true,
    rightSaved: columnWidths.right.current,
    rightCurrent,
    hasResizedColumns: true,
    centerCurrent: viewportWidth - leftCurrent - rightCurrent,
  };
}

/**
 * Counts document words while preserving the legacy single-newline-as-empty behavior.
 *
 * @param {string} text Document text.
 * @param {Function} countWordsFn Word-counting implementation.
 * @returns {number} Word count.
 */
export function getWordCountFromText(text: string, countWordsFn: CountWordsFn): number {
  if (text === "\n") {
    return 0;
  }

  return countWordsFn(text);
}

/**
 * Selection updates are debounced, so they can fire after TipTap has switched or
 * torn down the editor. Ignore those stale events before they can update React
 * state and touch iframe portals during cleanup.
 */
export function shouldHandleTiptapSelectionUpdate(
  currentEditor: TiptapSelectionEditor | null | undefined,
  eventEditor: TiptapSelectionEditor | null | undefined,
  componentUnmounted: boolean
): boolean {
  if (componentUnmounted || !currentEditor || currentEditor.isDestroyed || !currentEditor.view?.state) {
    return false;
  }

  return !eventEditor || eventEditor === currentEditor;
}

/**
 * Builds a highlighted-word-count state update when the current selection changes count.
 *
 * @param {{ length: number, text: string } | null} range Current selection range.
 * @param {number | null} currentHighlightedWordCount Existing highlighted word count in state.
 * @param {Function} countWordsFn Word-counting implementation.
 * @returns {{ highlightedWordCount: number | null } | null} Partial state update, or null when unchanged.
 */
export function getHighlightedWordCountState(
  range: SelectionRange | null,
  currentHighlightedWordCount: number | null,
  countWordsFn: CountWordsFn
): HighlightedWordCountState | null {
  if (range && range.length > 0) {
    const highlightedWordCount = countWordsFn(range.text);
    if (currentHighlightedWordCount !== highlightedWordCount) {
      return { highlightedWordCount };
    }

    return null;
  }

  if (currentHighlightedWordCount !== null) {
    return { highlightedWordCount: null };
  }

  return null;
}

/**
 * Derives which selection actions are available for the current highlighted word count.
 *
 * @param {number} words Highlighted word count.
 * @returns {object} Selection affordance flags and side-effect hints.
 */
export function getSelectionActionState(words: number): SelectionActionState {
  return {
    rewriteSelection: words >= MIN_REWRITE_WORDS && words <= MAX_REWRITE_WORDS,
    expandSelection: words >= MIN_EXPAND_WORDS && words <= MAX_EXPAND_WORDS,
    visualizeSelection: words >= MIN_VISUALIZE_WORDS && words <= MAX_VISUALIZE_WORDS,
    describeSelection: words >= MIN_DESCRIBE_WORDS && words <= MAX_DESCRIBE_WORDS,
    commandMenuSelection: words >= MIN_QUICK_EDIT_WORDS && words <= MAX_QUICK_EDIT_HIGHLIGHTED_WORDS,
    shouldFetchRelatedWords: words <= 2 && words > 0,
    shouldSendTextSelectedSignal: words >= 40,
  };
}

/**
 * Builds the selection state for a collapsed cursor range.
 *
 * @param {object} range Collapsed editor selection range.
 * @returns {object} Partial state update that clears selection affordances.
 */
export function getCollapsedSelectionState<TRange>(range: TRange): CollapsedSelectionState<TRange> {
  return {
    expandSelection: false,
    describeSelection: false,
    rewriteSelection: false,
    visualizeSelection: false,
    commandMenuSelection: false,
    relatedClusters: null,
    selectionRange: range,
  };
}

/**
 * Builds the selection state for clearing the current selection entirely.
 *
 * @returns {object} Partial state update that clears selection data and affordances.
 */
export function getClearedSelectionState(): ClearedSelectionState {
  return {
    selectionRange: null,
    expandSelection: false,
    describeSelection: false,
    rewriteSelection: false,
    visualizeSelection: false,
    commandMenuSelection: false,
    relatedClusters: null,
    selection: null,
    highlightedWordCount: null,
  };
}

/**
 * Builds selection-related state resets after document text changes.
 *
 * @param {string} text Current document text.
 * @returns {object} Partial state update.
 */
export function getTextChangeResetState(text: string): TextChangeResetState {
  return {
    expandSelection: false,
    describeSelection: false,
    visualizeSelection: false,
    rewriteSelection: false,
    commandMenuSelection: false,
    relatedClusters: null,
    ...(text ? { commandModeState: "off" as const } : {}),
  };
}

/**
 * Builds project-store UI updates after document text changes.
 *
 * @param {string} text Current document text.
 * @returns {{ hideLinkingMenu: boolean, showNewDocumentCTAs: boolean }} Project UI flags.
 */
export function getTextChangeProjectUiState(text: string): TextChangeProjectUiState {
  return {
    hideLinkingMenu: Boolean(text),
    showNewDocumentCTAs: !text,
  };
}

/** ProseMirror empty TipTap docs report `doc.content.size === 2`. */
export const STORY_BIBLE_EMPTY_DOCUMENT_CONTENT_SIZE = 2;

/**
 * Smallest `doc.content.size` once the document has any text. Story Bible only
 * distinguishes empty (`=== 2`) vs nonempty (`> 2`), so we clamp all nonempty
 * sizes to this stable sentinel to avoid re-rendering on every keystroke.
 */
export const STORY_BIBLE_NONEMPTY_DOCUMENT_CONTENT_SIZE = 3;

export function getStoryBibleDocumentLengthForContentState(
  documentLength: number | null | undefined
): number | null | undefined {
  if (typeof documentLength !== "number" || !Number.isFinite(documentLength)) {
    return documentLength;
  }

  return documentLength > STORY_BIBLE_EMPTY_DOCUMENT_CONTENT_SIZE
    ? STORY_BIBLE_NONEMPTY_DOCUMENT_CONTENT_SIZE
    : documentLength;
}

/**
 * Keeps the caret in the editor when a popover closes because the user
 * clicked into the manuscript.
 *
 * Radix's modal popovers return focus to their trigger via onCloseAutoFocus.
 * When the close was caused by a pointerdown inside the editor container, that
 * default would yank focus back out of the editor — so close-autofocus
 * handlers call this to preventDefault in that case. (Successor of the
 * iframe-era `keepFocusInEditorIfForwardedFromIframe`.)
 */
const EDITOR_POINTER_WINDOW_MS = 200;
let lastPointerDownInEditorAt = 0;
let editorPointerTrackerInstalled = false;

function ensureEditorPointerTracker(): void {
  if (editorPointerTrackerInstalled || typeof document === "undefined") return;
  editorPointerTrackerInstalled = true;
  document.addEventListener(
    "pointerdown",
    (event) => {
      const target = event.target;
      if (
        target instanceof Node &&
        document.getElementById("editorContainer")?.contains(target)
      ) {
        lastPointerDownInEditorAt = Date.now();
      }
    },
    true
  );
}

export function wasPointerDownInEditorRecently(): boolean {
  ensureEditorPointerTracker();
  return Date.now() - lastPointerDownInEditorAt < EDITOR_POINTER_WINDOW_MS;
}

export function keepFocusInEditorIfEventFromEditor(event: Event): void {
  if (wasPointerDownInEditorRecently()) {
    event.preventDefault();
  }
}

// Install the tracker as soon as this module loads so the first popover close
// already has pointer history to consult.
ensureEditorPointerTracker();
